import { getMediaTypeFromSpecifier } from "@/util/mediaTypes.ts";
import { RawMediaItem, MediaItem, MediaCollection } from "@/types/media.ts";

function determineType(item: RawMediaItem) {
  if (!item.isExternal) {
    const type = getMediaTypeFromSpecifier(item.mimeType ?? "");
    if (!type) {
      console.warn(
        `Media item ${item._id} has an unknown mime type ${item.mimeType}. Treating as an image.`
      );
    } else {
      return type;
    }
  } else if (
    item.metadata.externalType &&
    ["youtube", "facebook", "vimeo", "hudl", "dacast"].includes(
      item.metadata.externalType
    )
  ) {
    return "video";
  } else {
    console.warn(
      `The media type of ${item._id} cannot be determined. Treating as an image.`
    );
  }
  return "image";
}

/*
This function enhances a media object with additional
properties. App components should always use this
enhanced object, not the raw media object.
object.
 */
export function transformMedia(rawItem: RawMediaItem): MediaItem {
  if (!rawItem.uuid && !rawItem.url) {
    throw new Error("Media item must have a uuid or url");
  }
  return {
    ...rawItem,
    type: determineType(rawItem),
    url: rawItem.uuid
      ? `https://media.rocketalumnisolutions.com/${rawItem.uuid}/`
      : (rawItem.url as string),
  };
}

/**
 * expands a collection of media objects if any object has grouped sub-items
 * e.g. a PDF with images.
 */
export function expandMediaCollection(
  collection: MediaCollection
): MediaCollection {
  return collection.reduce((newCollection: MediaCollection, item) => {
    if (item.type === "pdf") {
      const hasSubItems = item.metadata?.subItems?.length;
      newCollection.push({
        ...item,
        pdfStatus: hasSubItems ? "expanded" : "processing",
      });
      if (hasSubItems && !collection.find((i) => i.parentID === item._id)) {
        item.metadata.subItems!.forEach((subItem, index) => {
          // create a new raw item first.
          const newRawItem: RawMediaItem = {
            damReferences: [],
            people: [],
            references: [],
            tags: [],
            textEntities: [],
            url: null,
            collectionID: item.collectionID,
            isExternal: item.isExternal,
            uploadedAt: item.uploadedAt,
            siteID: item.siteID,
            uuid: subItem.uuid,
            mimeType: subItem.mimeType,
            name: subItem.name ?? `PDF page ${index + 1}`,
            metadata: {
              hideInDAM: false,
              subItems: [],
              externalThumbnail: "",
            },
            _id: String(subItem._id ?? `${item._id}-${index}`),
          };
          const newItem = transformMedia(newRawItem);
          newItem.parentID = item._id;
          newCollection.push(newItem);
        });
      }
    } else {
      newCollection.push(item);
    }
    return newCollection;
  }, []);
}
